import PropTypes from 'prop-types'
import { cn } from '../../lib/cn'

/**
 * Card component with optional header and footer
 *
 * @component
 * @example
 * <Card>
 *   <Card.Header>
 *     <h2>Title</h2>
 *   </Card.Header>
 *   <Card.Body>
 *     Content goes here
 *   </Card.Body>
 *   <Card.Footer>
 *     Footer content
 *   </Card.Footer>
 * </Card>
 */
export default function Card({
  children,
  variant = 'default',
  hoverable = false,
  className = '',
  ...props
}) {
  const cardClasses = cn(
    'bg-white rounded-lg overflow-hidden',
    variant === 'default' && 'shadow-md',
    variant === 'bordered' && 'border-2 border-gray-200',
    hoverable && 'hover:shadow-xl transition-shadow duration-200 cursor-pointer',
    className
  )

  return (
    <div className={cardClasses} {...props}>
      {children}
    </div>
  )
}

function CardHeader({ children, className = '', ...props }) {
  const headerClasses = cn(
    'px-6 py-4 border-b border-gray-200 bg-gray-50',
    className
  )

  return (
    <div className={headerClasses} {...props}>
      {children}
    </div>
  )
}

function CardBody({ children, className = '', ...props }) {
  const bodyClasses = cn('p-6', className)

  return (
    <div className={bodyClasses} {...props}>
      {children}
    </div>
  )
}

function CardFooter({ children, className = '', ...props }) {
  const footerClasses = cn(
    'px-6 py-4 border-t border-gray-200 bg-gray-50',
    className
  )

  return (
    <div className={footerClasses} {...props}>
      {children}
    </div>
  )
}

Card.Header = CardHeader
Card.Body = CardBody
Card.Footer = CardFooter

Card.propTypes = {
  /** Card content */
  children: PropTypes.node.isRequired,
  /** Visual variant */
  variant: PropTypes.oneOf(['default', 'bordered']),
  /** Adds hover effect */
  hoverable: PropTypes.bool,
  /** Additional CSS classes */
  className: PropTypes.string
}

CardHeader.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string
}

CardBody.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string
}

CardFooter.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string
}
